// FolderRow.jsx — collapsible folder row for CodeSidebar.
//
// Design inputs:
// - tastedesign: refined > decorative — folder uses the same neutral
//   palette as session rows; collapsed by default; chevron rotates on
//   expand (200ms ease).
// - impeccable: subtle hover state (bg-apolo-surface2/60) + active dot
//   for sessions inside; ring on focus.
// - ui-ux-pro-max: drag-drop target via dataTransfer (session.id payload);
//   keyboard: Enter/Space toggle, F2 rename inline, Backspace delete.
// - shad-pro: composability — row + children list, no internal padding
//   leakage; consumers control spacing.

import { useState, useRef, useEffect } from 'react';
import { ChevronRight, Folder, FolderOpen, MoreHorizontal, Trash2, X } from 'lucide-react';
import { useAgentStore } from '../../store/useAgentStore.js';

// Folder color palette — 8 tasteful, distinct hues that read well on the dark sidebar.
// Stored verbatim (hex) in code_session_folders.color; '' = no color (neutral default).
const FOLDER_COLORS = ['#f43f5e', '#f59e0b', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899'];
import { sessionKind } from '../../lib/code/sessionKind.js';
import {
  isSessionDragEvent,
  readSessionIdFromDataTransfer,
  setSessionDragData,
} from '../../lib/sessionDrag.js';

export default function FolderRow({ folder, sessions, activeId, onPickSession }) {
  const expanded = useAgentStore((s) => !!s.code.expandedFolders[folder.id]);
  const toggleExpanded = useAgentStore((s) => s.toggleFolderExpanded);
  const renameFolder = useAgentStore((s) => s.renameFolder);
  const deleteFolder = useAgentStore((s) => s.deleteFolder);
  const setFolderColor = useAgentStore((s) => s.setFolderColor);
  const moveSession = useAgentStore((s) => s.moveSessionToFolder);

  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(folder.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onClick(e) {
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('pointerdown', onClick);
    return () => document.removeEventListener('pointerdown', onClick);
  }, [menuOpen]);

  async function commitRename() {
    const next = nameDraft.trim();
    if (next && next !== folder.name) {
      await renameFolder(folder.id, next);
    } else {
      setNameDraft(folder.name);
    }
    setRenaming(false);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const sessionId = readSessionIdFromDataTransfer(e.dataTransfer);
    if (sessionId) moveSession(sessionId, folder.id);
  }

  return (
    <div className="select-none">
      <div
        onDragOver={(e) => {
          if (!isSessionDragEvent(e)) return;
          e.preventDefault();
          if (!dragOver) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={
          'group flex items-center gap-1 rounded-md px-1 transition ' +
          (dragOver
            ? 'bg-apolo-accent/15 ring-1 ring-apolo-accent/40'
            : 'hover:bg-apolo-surface2/60')
        }
      >
        <button
          type="button"
          onClick={() => toggleExpanded(folder.id)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Recolher pasta' : 'Expandir pasta'}
          className="flex flex-1 items-center gap-1.5 rounded-md px-1 py-1.5 text-left text-xs text-apolo-textSecondary focus:outline-none focus:ring-2 focus:ring-apolo-accent/40"
        >
          <ChevronRight
            size={11}
            aria-hidden="true"
            className={'shrink-0 text-apolo-textTertiary transition-transform duration-200 ' + (expanded ? 'rotate-90' : '')}
          />
          {(() => {
            const Icon = expanded ? FolderOpen : Folder;
            const tint = folder.color || '';
            return (
              <Icon
                size={12}
                aria-hidden="true"
                className={'shrink-0 ' + (tint ? '' : (expanded ? 'text-apolo-accent' : 'text-apolo-textTertiary'))}
                style={tint ? { color: tint } : undefined}
              />
            );
          })()}
          {renaming ? (
            <input
              ref={inputRef}
              type="text"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                else if (e.key === 'Escape') {
                  setNameDraft(folder.name);
                  setRenaming(false);
                }
              }}
              className="flex-1 min-w-0 rounded border border-apolo-borderFocus bg-apolo-surface2 px-1 py-0 text-xs text-apolo-textPrimary focus:outline-none"
            />
          ) : (
            <span className="flex-1 min-w-0 truncate">{folder.name}</span>
          )}
          {sessions.length > 0 && !renaming && (
            <span className="shrink-0 text-[10px] text-apolo-textTertiary tabular-nums">
              {sessions.length}
            </span>
          )}
        </button>
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Mais opções"
            className="hidden h-6 w-6 items-center justify-center rounded-md text-apolo-textTertiary opacity-0 transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary group-hover:flex group-hover:opacity-100 focus:flex focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-apolo-accent/40"
          >
            <MoreHorizontal size={11} aria-hidden="true" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-7 z-40 w-44 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 py-1 shadow-xl">
              <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5" role="group" aria-label="Cor da pasta">
                {FOLDER_COLORS.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    aria-label={`Colorir pasta ${hex}`}
                    title="Colorir pasta"
                    onClick={() => { setMenuOpen(false); setFolderColor(folder.id, hex); }}
                    className={'h-4 w-4 rounded-full transition hover:scale-110 focus:outline-none focus:ring-2 focus:ring-apolo-accent/40 ' + (folder.color === hex ? 'ring-2 ring-apolo-textPrimary/70' : '')}
                    style={{ backgroundColor: hex }}
                  />
                ))}
                {folder.color ? (
                  <button
                    type="button"
                    aria-label="Remover cor"
                    title="Sem cor"
                    onClick={() => { setMenuOpen(false); setFolderColor(folder.id, ''); }}
                    className="flex h-4 w-4 items-center justify-center rounded-full border border-apolo-borderSubtle text-apolo-textTertiary transition hover:text-apolo-textPrimary"
                  >
                    <X size={9} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
              <div className="my-0.5 border-t border-apolo-borderSubtle" />
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setRenaming(true); }}
                className="w-full px-2 py-1 text-left text-xs text-apolo-textSecondary hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
              >
                Renomear
              </button>
              <button
                type="button"
                onClick={async () => { setMenuOpen(false); if (window.confirm(`Deletar pasta "${folder.name}"? As sessões voltam para Recents.`)) await deleteFolder(folder.id); }}
                className="inline-flex w-full items-center gap-1 px-2 py-1 text-left text-xs text-apolo-error hover:bg-apolo-error/10"
              >
                <Trash2 size={10} aria-hidden="true" /> Deletar
              </button>
            </div>
          )}
        </div>
      </div>

      {expanded && sessions.length > 0 && (
        <ul className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-apolo-borderSubtle pl-2">
          {sessions.map((s) => {
            // (E5) provenance parity — same Local/Cloud icon + branch chip the
            // Recents rows show, via the shared sessionKind helper. Icon null =
            // modelo não mapeado (silente).
            const kind = sessionKind(s?.model);
            return (
            <li key={s.id}>
              <button
                type="button"
                draggable
                onDragStart={(e) => {
                  setSessionDragData(e.dataTransfer, s.id);
                }}
                onClick={() => onPickSession(s.id)}
                className={
                  'flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs transition ' +
                  (s.id === activeId
                    ? 'bg-apolo-surface4 text-apolo-textPrimary'
                    : 'text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary')
                }
              >
                <span
                  aria-hidden="true"
                  className={'h-1.5 w-1.5 shrink-0 rounded-full ' + (s.id === activeId ? 'bg-apolo-accent' : 'bg-apolo-textTertiary/40')}
                />
                {kind.Icon ? (
                  <kind.Icon
                    size={11}
                    className="shrink-0 text-apolo-textTertiary"
                    aria-label={`Execução ${kind.label}`}
                  />
                ) : null}
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                {s.branch && (
                  <span
                    className="ml-1 shrink-0 truncate font-mono text-[10px] text-apolo-textTertiary"
                    style={{ maxWidth: 76 }}
                    title={s.branch}
                  >
                    {s.branch}
                  </span>
                )}
              </button>
            </li>
            );
          })}
        </ul>
      )}
      {expanded && sessions.length === 0 && (
        <p className="ml-3 border-l border-apolo-borderSubtle pl-3 py-1 text-[10px] text-apolo-textTertiary">
          Arraste uma sessão aqui
        </p>
      )}
    </div>
  );
}
